
import React, { useState } from 'react';
import { Vehicle } from '../types';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { VehicleActions } from './VehicleActions';
import { useVehicleImages } from '../hooks/useVehicles';
import { Link } from 'react-router-dom';
import { generateSlug } from '@/utils/vehicleSlug';

interface VehicleCardProps {
  vehicle: Vehicle;
  isAdmin?: boolean;
  onEdit?: (vehicle: Vehicle) => void;
  onDelete?: (id: string) => void;
}

export const VehicleCard = React.memo(({ vehicle, isAdmin, onEdit, onDelete }: VehicleCardProps) => {
  const [loadImages, setLoadImages] = useState(false);
  const { data: images = [] } = useVehicleImages(loadImages ? vehicle.id : null);
  
  // Use existing images from vehicle data or fetched images
  const vehicleImages = vehicle.images?.length > 0 ? vehicle.images : images;
  const formatPrice = (price: number) => {
    if (!price || price === 0) {
      return "Prix sur demande";
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryLabel = (category: Vehicle['category']) => {
    const labels = {
      'camion': 'Camion',
      'materiel-industriel': 'Matériel industriel',
      'utilitaire': 'Utilitaire',
      'remorque': 'Remorque',
      'autre': 'Autre'
    };
    return labels[category];
  };

  const isNewVehicle = () => {
    const dateAdded = new Date(vehicle.dateAdded);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return dateAdded > thirtyDaysAgo;
  };

  const slug = generateSlug(vehicle);
  
  const cardContent = (
    <>
      <div className="relative overflow-hidden group">
        <img
          src={vehicleImages[0] || '/placeholder.svg'}
          alt={`${vehicle.title} - ${vehicle.category} d'occasion ${vehicle.year ? `année ${vehicle.year}` : ''} ${vehicle.euroStandard ? `Euro ${vehicle.euroStandard}` : ''} ${vehicle.critAir ? `Crit'Air ${vehicle.critAir}` : ''} - Trucks 52 SARL Val-de-Meuse Haute-Marne`.trim()}
          className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
          width={284}
          height={192}
          loading="lazy"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          onError={(e) => {
            e.currentTarget.src = '/placeholder.svg';
          }}
        />
        {/* Overlay gradient au hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Badge "Voir la fiche" au hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
          <Badge className="bg-primary text-primary-foreground text-sm px-4 py-2 shadow-lg">
            Voir la fiche détaillée →
          </Badge>
        </div>
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
          <Badge variant="secondary" className="bg-white/95 backdrop-blur-sm text-gray-800 shadow-md font-semibold">
            {getCategoryLabel(vehicle.category)}
          </Badge>
          {isNewVehicle() && (
            <Badge className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold shadow-lg animate-pulse">
              ✨ NOUVEAU
            </Badge>
          )}
          {vehicle.isVerified && !isAdmin && (
            <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold shadow-lg">
              ✓ Vérifié
            </Badge>
          )}
        </div>
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/95 hover:bg-white shadow-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit?.(vehicle);
              }}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="bg-red-500/95 hover:bg-red-600 shadow-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete?.(vehicle.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      <CardContent className="p-6">
        <h3 className="font-bold text-xl mb-3 line-clamp-2 group-hover:text-primary transition-colors">{vehicle.title}</h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3 leading-relaxed">{vehicle.description}</p>
        
        {/* Specs rapides */}
        <div className="flex flex-wrap gap-2 mb-4">
          {vehicle.year && vehicle.year !== 'avant-1990' && (
            <Badge variant="outline" className="text-xs">
              📅 {vehicle.year}
            </Badge>
          )}
          {vehicle.mileage && (
            <Badge variant="outline" className="text-xs">
              🛣️ {vehicle.mileage.toLocaleString('fr-FR')} km
            </Badge>
          )}
          {vehicle.enginePower && (
            <Badge variant="outline" className="text-xs">
              ⚡ {vehicle.enginePower} ch
            </Badge>
          )}
        </div>
        
        <div className="text-xs text-muted-foreground border-t pt-3">
          Ajouté le {formatDate(vehicle.dateAdded)}
        </div>
      </CardContent>
      
      <CardFooter className="p-6 pt-0 flex-col">
        <div className="w-full mb-4 pb-4 border-b">
          {vehicle.showPrice !== false && (
            vehicle.priceHT && vehicle.priceTTC ? (
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {formatPrice(vehicle.priceTTC)}
                </div>
                <div className="text-sm text-muted-foreground">
                  TTC ({formatPrice(vehicle.priceHT)} HT)
                </div>
              </div>
            ) : (
              <div className={`text-center text-2xl font-bold ${!vehicle.price || vehicle.price === 0 ? 'text-muted-foreground' : 'text-primary'}`}>
                {formatPrice(vehicle.price)}
              </div>
            )
          )}
        </div>
        
        {/* Actions de partage et contact seulement en mode client */}
        {!isAdmin && (
          <div className="w-full">
            <VehicleActions vehicle={vehicle} />
          </div>
        )}
      </CardFooter>
    </>
  );

  // Wrapper avec Link pour les utilisateurs, Card pour les admins
  return isAdmin ? (
    <Card 
      className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-primary"
      onMouseEnter={() => setLoadImages(true)}
    >
      {cardContent}
    </Card>
  ) : (
    <Link 
      to={`/vehicule/${slug}`} 
      className="block group"
      onMouseEnter={() => setLoadImages(true)}
    >
      <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 hover:border-primary">
        {cardContent}
      </Card>
    </Link>
  );
});
